import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../../WebDevelopmentPage/components/AnimatedSection";
import { fdModules, ModuleFeature } from "../fdModulesData";
import { fdFeatureIconMap } from "../fdModuleIcons";

const FeatureBlock: React.FC<{ feature: ModuleFeature }> = ({ feature }) => {
  const Icon = fdFeatureIconMap[feature.icon];

  return (
    <div className="hms-module-feature fd-module-feature">
      <div className="fd-module-feature__head">
        <span className="fd-module-feature__icon" aria-hidden="true">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <h4 className="hms-module-feature__title fd-module-feature__title">{feature.title}</h4>
      </div>
      {feature.description && <p className="hms-module-feature__desc">{feature.description}</p>}
      {feature.items && (
        <ul className="hms-module-feature__list">
          {feature.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FdModulesSection: React.FC = () => {
  const [activeId, setActiveId] = useState(fdModules[0].id);
  const activeModule = fdModules.find((m) => m.id === activeId) ?? fdModules[0];

  return (
    <section className="hms-modules" aria-labelledby="fd-modules-title">
      <div className="container">
        <AnimatedSection className="wdev-section-head wdev-section-head--center">
          <h2
            id="fd-modules-title"
            className="wdev-section-head__title wdev-section-head__title--dark wdev-section-head__title--underline"
          >
            App Features
          </h2>
          <p className="hms-modules__lead">
            Separate feature sets for the customer app, delivery app, and admin merchant panel —
            each tailored to its user role.
          </p>
        </AnimatedSection>

        <div className="hms-modules__tabs" role="tablist" aria-label="Food delivery app modules">
          {fdModules.map((mod) => (
            <button
              key={mod.id}
              type="button"
              role="tab"
              aria-selected={activeId === mod.id}
              aria-controls={`fd-panel-${mod.id}`}
              id={`fd-tab-${mod.id}`}
              className={`hms-modules__tab ${activeId === mod.id ? "hms-modules__tab--active" : ""}`}
              onClick={() => setActiveId(mod.id)}
            >
              {mod.title.replace(" Features", "").replace(" for Merchant", "")}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule.id}
            id={`fd-panel-${activeModule.id}`}
            role="tabpanel"
            aria-labelledby={`fd-tab-${activeModule.id}`}
            className="hms-modules__panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="hms-modules__panel-head">
              <h3>{activeModule.title}</h3>
              <p>{activeModule.description}</p>
            </div>

            <div className="hms-modules__features">
              {activeModule.features.map((feature) => (
                <FeatureBlock key={feature.title} feature={feature} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default FdModulesSection;
